import { type ApolloError, gql, useQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  DraftPayment,
  ExportHistoryType,
  InvoiceDetails,
  InvoiceTag,
  LineItem,
  Pagination,
  Payment,
  Supplier,
  Transaction,
} from "@/apis/gql/types";
import type { ServerFilters } from "@/hooks/useQueryFilters";
import { useTenant } from "@/providers/session";

import { useSpinDelay } from "spin-delay";
import { TABLE_INVOICE_FRAGMENT, type TableInvoiceFragment } from "./graphql";

export type UseGetAllInvoicesReturn = {
  data: UseGetAllInvoicesData;
  loading: boolean;
  error: ApolloError | undefined;
  fetchInvoices: (params?: FetchInvoicesParams) => Promise<void>;
  pagination: Pagination;
};

export type UseGetAllInvoicesData = Array<{
  invoiceId: string;
  data: GetAllInvoiceInvoice;
  createdAt: TableInvoiceFragment["createdAt"];
  tenantId: TableInvoiceFragment["tenantId"];
}>;

export const useGetAllInvoices = ({
  enabled = true,
  query = {},
  page = 1,
  limit = 50,
  global = "",
  sort = "createdAt",
  order = "desc",
}: UseGetAllInvoicesParams): UseGetAllInvoicesReturn => {
  const { tenantId } = useTenant();
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: page,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: limit,
    hasNextPage: false,
    hasPreviousPage: false,
    nextPage: page,
    previousPage: page > 1 ? page - 1 : 1,
  });

  const { data, previousData, error, loading, refetch } =
    useQuery<GetAllInvoicesQuery>(INVOICES_QUERY, {
      variables: {
        tenantId,
        query,
        global,
        page,
        limit,
        offset: (page - 1) * limit,
        sort,
        order,
      },
      skip: !enabled,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      onError: (error) => {
        Sentry.captureException(error);
      },
    });

  const fetchInvoices = useCallback(
    async (params?: FetchInvoicesParams) => {
      const {
        page: _page = page,
        limit: _limit = limit,
        global: _global = global,
        query: _query = query,
        sort: _sort = sort,
        order: _order = order,
      } = params || {};
      const mergedQuery = { ...query, ..._query };

      const offset = (_page - 1) * _limit;

      await refetch({
        page: _page,
        limit: _limit,
        global: _global,
        query: mergedQuery,
        offset: offset,
        sort: _sort,
        order: _order,
      });
    },
    [refetch, page, limit, global, query, order, sort],
  );

  useEffect(() => {
    if (data?.invoices) {
      const pagination = data.invoices.pagination;
      setPagination(pagination);
    }
  }, [data]);

  const invoices = useMemo(() => {
    const safeData = data || previousData;

    if (!safeData?.invoices) return [];

    return safeData.invoices.invoices.map((invoice) => {
      return {
        invoiceId: invoice.invoiceDetailsId,
        data: invoice,
        createdAt: invoice.invoice.createdAt,
        tenantId: invoice.invoice.tenantId,
      };
    });
  }, [data]);

  const isLoading = useSpinDelay(loading, { delay: 600, minDuration: 400 });

  return {
    data: invoices,
    loading: isLoading,
    error,
    fetchInvoices,
    pagination,
  };
};

export type GetAllInvoiceInvoice = {
  invoiceDetailsId: string; // This is the same as invoiceId but kept because it's used as the key field
  invoiceId: string;
  invoice: TableInvoiceFragment;
  lineItems: Pick<LineItem, "lineItemId" | "buJustification" | "accountId">[];
  tags: Pick<
    InvoiceTag,
    | "tagId"
    | "tagName"
    | "llmRule"
    | "details"
    | "approval"
    | "createdAt"
    | "updatedAt"
  >[];
  payments: Payment[];
  draftPayments: Pick<
    DraftPayment,
    | "draftPaymentId"
    | "tenantId"
    | "status"
    | "amount"
    | "rawAmount"
    | "currency"
    | "createdAt"
    | "recipientName"
    | "recipientIban"
    | "purpose"
    | "executionDate"
    | "discount"
    | "invoices"
  >[];
  supplier: Pick<
    Supplier,
    "supplierId" | "supplierName" | "status" | "address"
  >;
  exports: Pick<
    ExportHistoryType,
    "datevExportId" | "exportedAt" | "results"
  >[];
  transactions: Pick<Transaction, "bankTxnId" | "smbAttachmentStatus">[];
};

export type GetAllInvoicesQuery = {
  invoices: {
    invoices: GetAllInvoiceInvoice[];
    pagination: Pagination;
  };
};

export const INVOICES_QUERY = gql`
  query GetAllInvoices(
    $tenantId: String
    $page: Int
    $limit: Int
    $offset: Int
    $query: JSON
    $global: String
    $sort: String
    $order: String
  ) {
    invoices(
      tenantId: $tenantId
      page: $page
      limit: $limit
      offset: $offset
      query: $query
      global: $global
      sort: $sort
      order: $order
    ) {
      invoices {
        invoiceDetailsId
        invoiceId
        invoice {
          ...TableInvoice
        }
        lineItems {
          lineItemId
          buJustification
          accountId
        }
        tags {
          tagId
          tagName
          llmRule
          details
          approval
          createdAt
          updatedAt
        }
        payments {
          bankTxnProjectionId
          bankTxnId
          tenantId
          status
          amount
          createdAt
          recipientName
          recipientIban
          sequenceNumber
          senderIban
          purpose
          currency
          executionDate
        }
        draftPayments {
          draftPaymentId
          tenantId
          status
          amount
          rawAmount
          currency
          createdAt
          recipientName
          recipientIban
          purpose
          executionDate
          discount
          invoices {
            invoiceId
            invoiceNo
            totalAmount
          }
        }
        supplier {
          supplierId
          supplierName
          status
          address {
            addressId
            addressLine1
            city
            state
            postcode
            country
          }
        }
        exports {
          datevExportId
          results
          exportedAt
        }
        transactions {
          smbAttachmentStatus
          bankTxnId
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
        nextPage
        previousPage
      }
    }
  }
  ${TABLE_INVOICE_FRAGMENT}
`;

export type UseGetAllInvoicesParams = {
  enabled?: boolean;
  query?: ServerFilters;
  page?: number;
  limit?: number;
  global?: string;
  sort?: string;
  order?: "asc" | "desc";
  skipDbClear?: boolean;
};

export interface FetchInvoicesParams {
  page?: number;
  limit?: number;
  global?: string;
  query?: ServerFilters;
  sort?: string;
  order?: "asc" | "desc";
}
